import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	inject,
	Input,
	OnDestroy,
	OnInit,
	Output,
	signal,
} from '@angular/core';
import {
	ICmmsFilterComponent,
	ICmmsFilterEmittedValue,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/interfaces/filter-component.interface';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { SelectionModel } from '@angular/cdk/collections';
import { IOption } from '@shared/interfaces/selectable-options.interfaces';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import {
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	takeUntil,
} from 'rxjs/operators';
import { getFilterCategories } from '@app/pages/cmms/configs/cmms-entity-categories';
import { Apollo } from 'apollo-angular';
import {
	filterOptionsQueryMap,
	IFilterOptionsQueryMapItem,
} from '@app/pages/cmms/components/cmms-sort-filters-grouping/cmms-applied-filters-bar/multiselect-filter/filter-options-query-map';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { MaintenanceSelectorService } from '@app/pages/cmms/components/au-maintenance-selector/maintenance-selector.service';
import { ILabel } from '@app/pages/settings/interfaces/labels.interfaces';
import { LabelsService } from '@app/shared/services/labels.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LabelListMenuComponent } from '@app/pages/cmms/components/shared-form-group-controls/cmms-labels-section/label-list-menu/label-list-menu.component';
import { AuSearchInputComponent } from '@shared/ui-components/au-search-input/au-search-input.component';
import { AsyncPipe, CommonModule } from '@angular/common';
import { NgxTolgeeModule } from '@tolgee/ngx';

const nsMultiselectFilterConfig = {
	costAllocation: 'CostAllocationChoices',
	consequenceRating: 'ConsequenceRatingChoices',
	riskLevel: 'RiskLevelChoices',
	timeframe: 'TimeframeChoices',
	deviationType: 'DeviationTypeChoices',
};

@Component({
	selector: 'multiselect-filter',
	templateUrl: './multiselect-filter.component.html',
	styleUrls: ['./multiselect-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,

	imports: [
		CommonModule,
		NgxTolgeeModule,
		AsyncPipe,
		AuSearchInputComponent,
		LabelListMenuComponent,
		MatCheckboxModule,
		MatProgressSpinnerModule,
		NgxTolgeeModule,
		CommonModule,
	],
})
export class MultiselectFilterComponent
	implements ICmmsFilterComponent, OnInit, OnDestroy
{
	@Input() data: {
		activePage: CmmsPages;
		columnName: string;
		preselectedValue: string[];
	};
	@Output() filterValueChanged = new EventEmitter<ICmmsFilterEmittedValue>();

	loadingOptions = true;
	searchText$ = new BehaviorSubject('');
	filterOptions: IOption[] = [];
	selectionModel = new SelectionModel<IOption>(true, []);

	private filterQueryConfig;
	private destroy$ = new Subject<void>();
	readonly isLabelFilter = signal(false);
	readonly isSimpleFilter = signal(false);
	selectedLabelsPks = signal<string[]>([]);
	readonly nsMultiselectFilterConfig = nsMultiselectFilterConfig;
	private readonly maintenanceService = inject(MaintenanceSelectorService);
	private readonly labelsService = inject(LabelsService);
	labels = this.labelsService.labels;

	constructor(
		private apollo: Apollo,
		private cdr: ChangeDetectorRef,
		private notify: IconSnackBarService
	) {}

	ngOnInit(): void {
		this.filterQueryConfig = filterOptionsQueryMap[this.data.columnName];

		// TODO This is so fcking wrong, preselectedValue works only due to a bug...
		//  SearchText emits twice cause it's initialValue is '', and queryFilterOptions
		//  is called twice accordingly, if we skip the initialValue of searchText$ and use
		//  queryFilterOptions only once on init preselectedValue will not be applied.
		//  Good luck in fixing, as it's used not only in cmms but also for views and God knows where else...
		this.searchText$
			.pipe(
				takeUntil(this.destroy$),
				map(searchText => searchText.trim()),
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe(searchText => {
				this.queryFilterOptions(this.data.columnName, searchText);
			});

		this.selectionModel.changed.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.filterValueChanged.emit(
				this.generateFilterValue(this.selectionModel.selected)
			);
		});
	}

	queryFilterOptions(columnName: string, searchText?: string) {
		this.loadingOptions = true;
		// TODO Query the real data from BE later
		if (columnName === 'labels') {
			this.isLabelFilter.set(true);
			this.loadingOptions = false;
			if (this.data.preselectedValue) {
				this.selectionModel.clear();
				this.labels()?.forEach(label => {
					if (this.data.preselectedValue.includes(label.pk)) {
						this.selectionModel.select({ id: label.pk, name: label.name });
					}
				});
				this.selectedLabelsPks.set(this.data.preselectedValue);
			}
			this.cdr.markForCheck();

			return;
		}
		this.isSimpleFilter.set(true);

		if (columnName === 'category') {
			this.loadingOptions = false;
			this.cdr.markForCheck();
			this.filterOptions = getFilterCategories();
			this.applyPreselectedValue();
			return;
		}

		if (this.data.columnName === 'conditionGrade_In') {
			this.prepareConfigurationsForCG();
			return;
		}

		this.applyPreselectedValue();
		if (nsMultiselectFilterConfig[columnName]) {
			this.maintenanceService
				.getConfiguration(nsMultiselectFilterConfig[columnName])
				.pipe(
					takeUntil(this.destroy$),
					filter(value => !!value)
				)
				.subscribe(config => {
					this.loadingOptions = false;
					const noneOption = { id: '', name: 'none' };
					this.filterOptions = config.enumValues.map(({ name, description }) => ({
						id: name,
						name: description,
					}));
					this.filterOptions = [noneOption, ...this.filterOptions];
					// apply it again to make sure that preselected value have the same object reference
					this.applyPreselectedValue();
					this.cdr.markForCheck();
				});
			return;
		}

		const queryVariables = {
			...this.filterQueryConfig.queryVariables,
			search: searchText,
		};

		this.apollo
			.query({
				query: this.filterQueryConfig.query,
				variables: queryVariables,
			})
			.pipe(
				takeUntil(this.destroy$),
				map(({ data }) => this.prepareOptionsFromResponse(data, this.filterQueryConfig))
			)
			.subscribe({
				next: options => this.loadOptionsSuccess(options),
				error: err => this.loadOptionsError(err),
			});
	}

	isChecked(option: IOption): boolean {
		return this.selectionModel.isSelected(option);
	}

	selectedLabels() {
		return this.selectionModel.selected;
	}

	labelsSelectionChangedHandler(event: { pks: string[]; labels: ILabel[] }) {
		this.selectionModel.clear();
		event.labels.forEach(label =>
			this.selectionModel.select({ id: label.pk, name: label.name })
		);
		this.selectedLabelsPks.set(event.pks);
	}

	toggleSelection(evt: MatCheckboxChange, option: IOption): void {
		if (evt.checked) {
			this.selectionModel.select(option);
		} else {
			this.selectionModel.deselect(option);
		}
	}

	loadOptionsError(err: any): void {
		this.loadingOptions = false;
		console.error(err);
		this.notify.error('Cannot load options');
	}

	loadOptionsSuccess(options: IOption[]): void {
		this.loadingOptions = false;
		this.filterOptions = options;
		this.cdr.markForCheck();
		this.applyPreselectedValue();
	}

	prepareOptionsFromResponse(data: any, config: IFilterOptionsQueryMapItem): IOption[] {
		return data?.[config.dataKey]?.edges.map(edge => {
			return {
				id: edge.node[config.idKey],
				name: edge.node[config.nameKey],
			};
		});
	}

	generateFilterValue(options: IOption[]): {
		columnName: string;
		label: string;
		data: string[];
	} {
		/* Label should be in specific format that is needed to show it in filter button. */
		const label = [];
		const data = [];

		options.forEach(option => {
			label.push(option.name);
			data.push(option.id);
		});

		return {
			columnName: this.data.columnName,
			label: label.join('; '),
			data,
		};
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private prepareConfigurationsForCG() {
		this.loadingOptions = false;
		this.filterOptions = [
			{ id: '-1', name: 'IU' },
			{ id: '0', name: '0' },
			{ id: '1', name: '1' },
			{ id: '2', name: '2' },
			{ id: '3', name: '3' },
		];
		this.applyPreselectedValue();
		this.cdr.markForCheck();
	}

	private applyPreselectedValue() {
		if (this.data.preselectedValue?.length) {
			const values: IOption[] = this.filterOptions.filter(option =>
				this.data.preselectedValue.includes(option.id)
			);

			this.selectionModel.clear();
			this.selectionModel.select(...values);
		}
	}
}
